/* Judge.me public widgets only. No raw/private review endpoints are used. */
(() => {
  if (!window.DPJudgeMeReviews?.stats) {
    const cache = new Map();
    const statsCache = new Map();
    const collectionCache = new Map();
    const scalar = (data, key) => {
      const value = data && typeof data === 'object' ? (data[key] ?? data.value ?? data.widget) : data;
      if (typeof value !== 'number' && typeof value !== 'string') return null;
      if (typeof value === 'string' && !/^\s*\d+(\.\d+)?\s*$/.test(value)) return null;
      const number = Number(value);
      return Number.isFinite(number) ? number : null;
    };
    const shuffle = (items, random = Math.random) => {
      const result = [...items];
      for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
      }
      return result;
    };
    const eligible = (items) => {
      const seen = new Set();
      return items.filter(review => {
        const key = review.id || [review.author, review.date, review.title, review.body].join('|');
        if (![4, 5].includes(review.rating) || !review.body.trim() || seen.has(key)) return false;
        seen.add(key); return true;
      });
    };
    const widgetHTML = data => typeof data === 'string' ? data : (data && (data.widget ?? data.html)) || '';
    const readWidget = data => {
      const html = widgetHTML(data);
      if (typeof html !== 'string' || !html.includes('<')) throw new Error('Unexpected Judge.me widget response');
      // Parse off-document; never insert vendor HTML/scripts into the page.
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const text = (node, selector) => node.querySelector(selector)?.textContent.trim() || '';
      const nodes = [...doc.querySelectorAll('.jdgm-rev')];
      const reviews = nodes.map(node => {
        const rating = Number(node.querySelector('.jdgm-rev__rating')?.getAttribute('data-score'));
        const timestamp = node.querySelector('.jdgm-rev__timestamp');
        return {
          id: node.getAttribute('data-review-id') || node.getAttribute('data-id') || '',
          rating,
          title: text(node, '.jdgm-rev__title'),
          body: text(node, '.jdgm-rev__body'),
          author: text(node, '.jdgm-rev__author'),
          date: timestamp?.getAttribute('data-content') || timestamp?.textContent.trim() || '',
          verified: node.getAttribute('data-verified-buyer') === 'true',
        };
      });
      const summary = doc.querySelector('[data-average-rating][data-number-of-reviews]');
      const average = summary ? Number(summary.getAttribute('data-average-rating')) : null;
      const count = summary ? Number(summary.getAttribute('data-number-of-reviews')) : null;
      const pages = [...doc.querySelectorAll('.jdgm-paginate__page[data-page], [data-number-of-pages]')]
        .map(node => Number(node.getAttribute('data-number-of-pages') || node.getAttribute('data-page'))).filter(Number.isFinite);
      return { reviews, average, count, pageSize: nodes.length, lastPage: Math.max(1, ...pages) };
    };
    const get = (config, endpoint, params = {}) => {
      const url = new URL('https://api.judge.me/api/v1/widgets/' + endpoint);
      url.searchParams.set('shop_domain', config.shop);
      url.searchParams.set('api_token', config.token); // PUBLIC token only, configured in the theme editor.
      for (const [key, value] of Object.entries(params)) url.searchParams.set(key, String(value));
      const key = url.toString();
      if (!cache.has(key)) {
        const promise = (async () => {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 10000);
          try {
            const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
            if (!response.ok) throw new Error('Judge.me request failed');
            const text = await response.text();
            try { return JSON.parse(text); } catch (error) { return text; }
          } finally { clearTimeout(timer); }
        })();
        cache.set(key, promise);
        promise.catch(() => cache.delete(key));
      }
      return cache.get(key);
    };
    const samplePages = (lastPage, budget) => {
      // Sample pages across the full published set, without a large page-number array.
      const pages = new Set();
      const available = Math.max(0, lastPage - 1);
      const number = Math.min(budget, available);
      while (pages.size < number) pages.add(2 + Math.floor(Math.random() * available));
      return [...pages];
    };
    const finiteCount = value => Number.isFinite(value) && value >= 0;
    const mapLimited = async (items, callback) => {
      const results = [];
      for (let i = 0; i < items.length; i += 4) {
        results.push(...await Promise.allSettled(items.slice(i, i + 4).map(callback)));
      }
      return results;
    };
    const collectionProducts = async config => {
      const read = template => {
        if (!template) throw new Error('Select a review collection');
        const products = JSON.parse(template.content?.textContent || template.textContent);
        if (!Array.isArray(products)) throw new Error('Invalid collection product list');
        return {
          products, total: Number(template.dataset.totalProducts), handle: template.dataset.collectionHandle,
          page: Number(template.dataset.currentPage) || 1, param: template.dataset.pageParam || 'page', next: template.dataset.nextUrl,
        };
      };
      const fetchPage = async link => {
        const url = new URL(link, window.location.href);
        if (url.origin !== window.location.origin) throw new Error('Invalid collection pagination URL');
        url.searchParams.delete('sections'); url.searchParams.set('section_id', config.section);
        const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 10000);
        try {
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Collection pagination unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          return read(doc.querySelector('[data-jdgm-collection-products]'));
        } finally { clearTimeout(timer); }
      };
      let data = read(config.collectionTemplate);
      const handle = data.handle;
      if (data.total > 25000) throw new Error('Collection exceeds Liquid pagination limit');
      if (data.page !== 1) {
        const first = new URL(window.location.href); first.searchParams.set(data.param, '1');
        data = await fetchPage(first);
      }
      const products = new Map(); const seen = new Set();
      for (let i = 0; i < 100; i++) {
        if (data.handle !== handle || seen.has(data.page)) throw new Error('Collection pagination mismatch');
        seen.add(data.page);
        for (const p of data.products) {
          if (!p.id) throw new Error('Invalid collection product');
          products.set(String(p.id), { ...p, id: String(p.id), count: Number(p.count), average: Number(p.average) });
        }
        if (!data.next) break;
        data = await fetchPage(data.next);
      }
      if (!finiteCount(data.total) || products.size !== data.total) throw new Error('Incomplete collection product list');
      return [...products.values()];
    };
    const sourceKey = config => [config.shop,config.token,config.scope,config.scope === 'collection' ? config.collectionTemplate?.dataset.collectionHandle : config.scope === 'product' ? config.product || '' : ''].join('|');
    const collectionSummary = config => {
      const key = sourceKey(config);
      if (!collectionCache.has(key)) {
        const promise = prepareCollection(config);
        collectionCache.set(key, promise); promise.catch(() => collectionCache.delete(key));
      }
      return collectionCache.get(key);
    };
    const prepareCollection = async config => {
      const products = await collectionProducts(config);
      // Product metadata supplies full collection totals without fetching every review.
      const missing = products.filter(p => !p.hasStats || !finiteCount(p.count) || (p.count > 0 && !(p.average > 0 && p.average <= 5)));
      const recovered = await mapLimited(missing, p => get(config, 'product_review', { external_id: p.id, per_page: 25, page: 1 }).then(readWidget));
      let complete = true;
      missing.forEach((p, index) => {
        const result = recovered[index];
        if (result.status === 'fulfilled' && finiteCount(result.value.count) && (result.value.count === 0 || result.value.average > 0 && result.value.average <= 5)) {
          p.count = result.value.count; p.average = result.value.average || 0; p.hasStats = true;
        } else complete = false;
      });
      const reviewed = products.filter(p => p.hasStats && finiteCount(p.count) && p.count > 0 && p.average > 0 && p.average <= 5);
      const total = reviewed.reduce((sum, p) => sum + p.count, 0);
      const weighted = reviewed.reduce((sum, p) => sum + p.average * p.count, 0);
      return { products, reviewed, complete, total, weighted };
    };
    const loadCollection = async config => {
      const { products, reviewed, complete, total, weighted } = await collectionSummary(config);
      const known = reviewed.length ? reviewed : products;
      const weightTotal = known.reduce((sum, p) => sum + (p.count > 0 ? p.count : 1), 0);
      const possible = known.reduce((sum, p) => sum + Math.max(1, Math.ceil((p.count || 0) / 25)), 0);
      const budget = Math.min(config.pages, possible);
      const requests = new Map();
      // Weight by review count to give products with more reviews a proportionate chance.
      for (let attempts = 0; requests.size < budget && attempts < budget * 100; attempts++) {
        let value = Math.random() * weightTotal;
        const p = known.find(p => (value -= p.count > 0 ? p.count : 1) < 0) || known[known.length - 1];
        if (!p) break;
        const page = 1 + Math.floor(Math.random() * Math.max(1, Math.ceil((p.count || 0) / 25)));
        requests.set(p.id + ':' + page, { external_id: p.id, per_page: 25, page });
      }
      const results = await mapLimited([...requests.values()], params => get(config, 'product_review', params).then(readWidget));
      const reviews = results.flatMap(r => r.status === 'fulfilled' ? r.value.reviews : []);
      return { count: complete ? total : null, average: complete && total ? weighted / total : null, reviews: eligible(reviews) };
    };
    const calculateStats = async config => {
      if (config.scope === 'collection') {
        const {complete,total,weighted} = await collectionSummary(config);
        return {count:complete ? total : null,average:complete && total ? weighted / total : null};
      }
      if (config.scope === 'product') {
        if (!config.product) throw new Error('Select a review product');
        const first = readWidget(await get(config,'product_review',{external_id:config.product,per_page:25,page:1}));
        return {count:finiteCount(first.count) ? first.count : config.count,average:Number.isFinite(first.average) && first.average > 0 ? first.average : config.average};
      }
      const [rating,count] = await Promise.all([get(config,'all_reviews_rating'),get(config,'all_reviews_count')]);
      return {average:scalar(rating,'all_reviews_rating'),count:scalar(count,'all_reviews_count')};
    };
    const stats = config => {
      const key = sourceKey(config);
      if (!statsCache.has(key)) {
        const promise = calculateStats(config);
        statsCache.set(key,promise);promise.catch(()=>statsCache.delete(key));
      }
      return statsCache.get(key);
    };
    const load = async config => {
      if (config.scope === 'collection') return loadCollection(config);
      if (config.scope === 'product') {
        if (!config.product) throw new Error('Select a review product');
        const params = { external_id: config.product, per_page: 25 };
        const first = readWidget(await get(config, 'product_review', { ...params, page: 1 }));
        const count = finiteCount(first.count) ? first.count : config.count;
        const average = Number.isFinite(first.average) && first.average > 0 ? first.average : config.average;
        const lastPage = Math.max(first.lastPage, finiteCount(count) ? Math.ceil(count / 25) : 1);
        const pages = samplePages(lastPage, config.pages - 1);
        const rest = await Promise.allSettled(pages.map(page => get(config, 'product_review', { ...params, page }).then(readWidget)));
        return { average, count, reviews: eligible([ ...first.reviews, ...rest.flatMap(r => r.status === 'fulfilled' ? r.value.reviews : []) ]) };
      }
      // Entire store: published product AND store reviews, with matching full-store totals.
      const [ratingResult, countResult, shopCountResult, productResult, shopResult] = await Promise.allSettled([
        get(config, 'all_reviews_rating'), get(config, 'all_reviews_count'), get(config, 'shop_reviews_count'),
        get(config, 'all_reviews_page', { review_type: 'product-reviews', page: 1 }).then(readWidget),
        get(config, 'all_reviews_page', { review_type: 'shop-reviews', page: 1 }).then(readWidget),
      ]);
      const rating = ratingResult.status === 'fulfilled' ? scalar(ratingResult.value, 'all_reviews_rating') : null;
      const count = countResult.status === 'fulfilled' ? scalar(countResult.value, 'all_reviews_count') : null;
      const shopCount = shopCountResult.status === 'fulfilled' ? scalar(shopCountResult.value, 'shop_reviews_count') : null;
      const groups = [ [productResult, 'product-reviews', finiteCount(count) && finiteCount(shopCount) ? count - shopCount : null], [shopResult, 'shop-reviews', shopCount] ];
      if (groups.every(([r]) => r.status !== 'fulfilled')) throw new Error('Reviews unavailable');
      const candidates = [];
      const reviews = [];
      for (const [result, type, total] of groups) {
        if (result.status !== 'fulfilled') continue;
        const first = result.value;
        reviews.push(...first.reviews);
        // All-reviews widgets do not document per_page; use the actual page size.
        const lastPage = Math.max(first.lastPage, finiteCount(total) && first.pageSize ? Math.ceil(total / first.pageSize) : 1);
        for (const page of samplePages(lastPage, config.pages - 1)) candidates.push({ type, page });
      }
      const rest = await Promise.allSettled(shuffle(candidates).slice(0, config.pages - 1).map(({type,page}) => get(config, 'all_reviews_page', { review_type: type, page }).then(readWidget)));
      for (const result of rest) if (result.status === 'fulfilled') reviews.push(...result.value.reviews);
      return { average: rating, count, reviews: eligible(reviews) };
    };
    const element = (tag, className, text) => {
      const node = document.createElement(tag); node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const render = (root, config, data) => {
      const chosen = shuffle(data.reviews).slice(0, config.cards);
      if (!chosen.length) return false;
      const grid = root.querySelector('.dp-reviews__grid');
      const fragment = document.createDocumentFragment();
      for (const review of chosen) {
        const card = element('article', 'dp-review-card');
        const stars = element('div', 'dp-review-card__stars', '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating));
        stars.setAttribute('aria-label', review.rating + ' out of 5 stars');
        if (config.dates && review.date) {
          // ISO timestamps use a stable date; free-form display dates stay unchanged.
          const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(review.date);
          const date = iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : review.date;
          stars.appendChild(element('span', 'dp-review-card__date', date));
        }
        card.appendChild(stars);
        if (review.title) card.appendChild(element('h3', 'dp-review-card__title', review.title));
        card.appendChild(element('div', 'dp-review-card__text dp-review-card__text--live', review.body));
        const author = (review.author ? '— ' + review.author : 'Customer') + (review.verified ? ', Verified customer' : '');
        card.appendChild(element('div', 'dp-review-card__author', author));
        fragment.appendChild(card);
      }
      const header = root.querySelector('.dp-reviews__header--live');
      header.replaceChildren();
      if (Number.isFinite(data.average) && data.average > 0 && finiteCount(data.count) && data.count > 0) {
        const star = element('span', 'dp-reviews__average-star', '★'); star.setAttribute('aria-hidden','true');
        header.append(star, document.createTextNode(' ' + Number(data.average.toFixed(2)) + ' out of 5 · Based on ' + Math.trunc(data.count).toLocaleString() + ' ' + (data.count === 1 ? 'review' : 'reviews')));
        header.hidden = false;
      } else header.hidden = true;
      root.querySelector('.dp-reviews__header--manual')?.setAttribute('hidden', '');
      grid.replaceChildren(fragment); root.hidden = false;
      return true;
    };
    const configFromRoot = root => ({
        shop: root.dataset.jdgmShop, token: root.dataset.jdgmToken?.trim(), scope: root.dataset.jdgmScope,
        product: root.dataset.jdgmProduct, cards: Math.max(1, Math.min(8, Number(root.dataset.jdgmCards) || 2)),
        section: root.dataset.jdgmSection, collectionTemplate: root.querySelector('[data-jdgm-collection-products]'),
        pages: Math.max(1, Math.min(10, Number(root.dataset.jdgmPages) || 4)), dates: root.dataset.jdgmDates === 'true',
        average: Number(root.dataset.jdgmAverage) || null, count: Number(root.dataset.jdgmCount) || null,
      });
    const init = async root => {
      if (root.dataset.jdgmEnabled !== 'true' || root.dataset.jdgmInitialized) return;
      root.dataset.jdgmInitialized = 'true';
      const config = configFromRoot(root);
      const fallback = () => {
        if (root.dataset.jdgmFallback !== 'true') return;
        const cards = [...root.querySelectorAll('.dp-review-card')].filter(card => {
          const stars = card.querySelector('.dp-review-card__stars');
          const rating = (stars?.childNodes[0]?.textContent.match(/★/g) || []).length;
          if (![4, 5].includes(rating)) { card.remove(); return false; }
          return true;
        });
        if (cards.length) root.hidden = false;
      };
      if (!config.token) { fallback(); return; }
      try {
        const [data,summary] = await Promise.all([load(config),stats(config).catch(()=>({average:null,count:null}))]);
        if (!render(root, config, {...data,...summary})) fallback();
      }
      catch (error) {
        fallback();
        // Do not log request URLs or tokens.
        if (window.Shopify?.designMode) console.info('DP reviews: check the public token, review source and selected product.');
      }
    };
    let starId = 0;
    const trustStars = (target, average) => {
      const ns = 'http://www.w3.org/2000/svg';
      const polygon = '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2';
      const svgNode = (tag,attrs={}) => {
        const node=document.createElementNS(ns,tag);
        for(const [name,value] of Object.entries(attrs))node.setAttribute(name,String(value));
        return node;
      };
      target.replaceChildren();
      for(let i=0;i<5;i++) {
        const fill=Math.max(0,Math.min(1,average-i));
        const svg=svgNode('svg',{viewBox:'0 0 24 24','aria-hidden':'true'});
        svg.appendChild(svgNode('polygon',{points:polygon,fill:'none',stroke:'currentColor','stroke-width':1.5}));
        if(fill>0) {
          const id='dp-trust-star-'+(++starId);
          const defs=svgNode('defs');const clip=svgNode('clipPath',{id});
          clip.appendChild(svgNode('rect',{x:0,y:0,width:24*fill,height:24}));defs.appendChild(clip);svg.appendChild(defs);
          svg.appendChild(svgNode('polygon',{points:polygon,fill:'currentColor','clip-path':`url(#${id})`}));
        }
        target.appendChild(svg);
      }
      target.setAttribute('aria-label',Number(average.toFixed(2))+' out of 5 stars');
      target.style.removeProperty('display');
    };
    const initTrust = async (root, force=false) => {
      const mode=root.dataset.statsSource;
      if(mode==='manual'||!mode||(root.dataset.statsInitialized&&!force))return;
      root.dataset.statsInitialized='true';
      const run=String(Number(root.dataset.statsRun||0)+1);root.dataset.statsRun=run;
      const title=root.querySelector('[data-dp-trust-count]');
      const sub=root.querySelector('[data-dp-trust-rating]');
      const stars=root.querySelector('[data-dp-trust-stars]');
      const content=root.querySelector('[data-dp-trust-stats]');
      if(!title||!sub||!stars||!content)return;
      content.style.visibility='hidden';
      try {
        let config;
        if(mode==='match_reviews') {
          const targets=[...document.querySelectorAll('[data-dp-judgeme-reviews]')].filter(node=>node.dataset.jdgmEnabled==='true');
          const target=root.dataset.reviewsTarget ? targets.find(node=>node.dataset.jdgmSection===root.dataset.reviewsTarget||node.id===root.dataset.reviewsTarget) : targets[0];
          if(!target)throw new Error('No live reviews section');
          config=configFromRoot(target);
        } else config=configFromRoot(root);
        if(!config.token)throw new Error('Public token required');
        const data=await stats(config);
        if(root.dataset.statsRun!==run||!root.isConnected)return;
        if(!finiteCount(data.count)||data.count>0&&!(Number.isFinite(data.average)&&data.average>0&&data.average<=5))throw new Error('Review statistics unavailable');
        title.textContent=Math.trunc(data.count).toLocaleString()+' '+(data.count===1?'review':'reviews');
        sub.textContent=data.count ? 'Rated '+Number(data.average.toFixed(2))+' out of 5' : 'No reviews yet';
        if(data.count)trustStars(stars,data.average);else stars.style.display='none';
        content.style.removeProperty('visibility');
      } catch(error) {
        if(root.dataset.statsRun!==run||!root.isConnected)return;
        title.textContent='Reviews unavailable';sub.textContent='';stars.style.display='none';content.style.removeProperty('visibility');
      }
    };
    window.DPJudgeMeReviews = { init, stats, initTrust };
    document.addEventListener('shopify:section:load', event => {
      event.target.querySelectorAll('[data-dp-judgeme-reviews]').forEach(init);
      event.target.querySelectorAll('[data-dp-review-trust]').forEach(node=>initTrust(node));
      document.querySelectorAll('[data-dp-review-trust][data-stats-source="match_reviews"]').forEach(node=>initTrust(node,true));
    });
    document.addEventListener('shopify:section:unload',()=>queueMicrotask(()=>document.querySelectorAll('[data-dp-review-trust][data-stats-source="match_reviews"]').forEach(node=>initTrust(node,true))));
  }
  document.querySelectorAll('[data-dp-judgeme-reviews]').forEach(window.DPJudgeMeReviews.init);
  document.querySelectorAll('[data-dp-review-trust]').forEach(root=>window.DPJudgeMeReviews.initTrust(root));
})();
